import React, { useState, useEffect, useRef } from 'react';
import { relancesAPI } from '../services/api';
import './Relances.css';

const CATEGORIES = [
  { key: 'all', label: 'Tous', color: null },
  { key: 'aucune_reponse', label: 'Aucune reponse', color: 'var(--red)' },
  { key: 'relance_interesse', label: 'Relance interesse', color: 'var(--tgg)' },
  { key: 'pas_pret', label: 'Pas pret', color: 'var(--pur)' },
  { key: 'reporte', label: 'Reporte', color: 'var(--blue)' },
  { key: 'sans_commentaire', label: 'Sans commentaire', color: 'var(--t3)' },
  { key: 'paye', label: 'Paye', color: 'var(--tgg)' },
  { key: 'pas_interesse', label: 'Pas interesse', color: 'var(--red)' }
];

const MONTHS = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEARS = [2024, 2025, 2026];

const Relances = () => {
  const [relances, setRelances] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showImportYearModal, setShowImportYearModal] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const importYearInputRef = useRef(null);
  const [formData, setFormData] = useState({
    phone: '',
    comment: '',
    day: new Date().getDate()
  });

  useEffect(() => {
    loadData();
  }, [year, month, categoryFilter]);

  useEffect(() => {
    if (showImportYearModal && importYearInputRef.current) {
      importYearInputRef.current.focus();
      importYearInputRef.current.select();
    }
  }, [showImportYearModal]);

  const loadData = async () => {
    try {
      const params = { year, month };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const response = await relancesAPI.getAll(params);
      if (response.data.success) {
        setRelances(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load relances', error);
    }
  };

  const parsePhoneNumber = (value) => {
    if (!value) return '';
    
    let cleanValue = value.toString().trim();
    
    // Handle scientific notation like "2,1266E+11" or "2,12623E+11"
    if (cleanValue.includes('E') || cleanValue.includes('e')) {
      cleanValue = cleanValue.replace(/,/g, '');
      const num = parseFloat(cleanValue);
      if (!isNaN(num)) {
        return num.toString();
      }
    }
    
    return cleanValue.replace(/[^0-9]/g, '');
  };

  const parseLine = (line) => {
    // Try tab first (Excel copy), then comma
    let cols = line.includes('\t') ? line.split('\t') : line.split(',');
    return cols.map(c => c.trim());
  };

  const startImportFlow = (file) => {
    if (!file) return;
    setPendingImportFile(file);
    setImportYear(year);
    setShowImportYearModal(true);
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    startImportFlow(file);
    event.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!pendingImportFile) return;
    setImporting(true);
    setImportStats(null);
    setShowImportYearModal(false);

    try {
      const response = await relancesAPI.importFile(pendingImportFile, importYear);
      
      if (response.data.success) {
        setImportStats({
          imported: response.data.data.imported,
          errors: response.data.data.errors,
          total: response.data.data.total
        });
        
        setTimeout(() => {
          loadData();
          setImportStats(null);
        }, 1500);
      } else {
        alert(response.data.message || 'Erreur lors de l\'importation');
      }

    } catch (error) {
      console.error('Import error:', error);
      alert('Erreur lors de l\'importation');
    }

    setImporting(false);
    setPendingImportFile(null);
  };

  const handleCancelImport = () => {
    if (importing) return;
    setShowImportYearModal(false);
    setPendingImportFile(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      startImportFlow(file);
    }
  };

  const openEditForm = (item = null) => {
    setEditingItem(item);
    setFormData({
      phone: item ? item.phone : '',
      comment: item ? item.comment : '',
      day: item ? item.day || new Date().getDate() : new Date().getDate()
    });
    setShowAddForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await relancesAPI.update(editingItem.id, {
          phone: formData.phone,
          year,
          month,
          day: formData.day,
          r1: editingItem.r1,
          r2: editingItem.r2,
          comment: formData.comment,
          category: editingItem.category
        });
      } else {
        await relancesAPI.create({
          phone: formData.phone,
          year,
          month,
          day: formData.day,
          r1: false,
          r2: false,
          comment: formData.comment,
          category: 'sans_commentaire'
        });
      }
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Failed to save', error);
    }
  };

  const toggleR1 = async (item) => {
    try {
      await relancesAPI.update(item.id, { ...item, r1: !item.r1 });
      loadData();
    } catch (error) {
      console.error('Failed to toggle R1', error);
    }
  };

  const toggleR2 = async (item) => {
    try {
      await relancesAPI.update(item.id, { ...item, r2: !item.r2 });
      loadData();
    } catch (error) {
      console.error('Failed to toggle R2', error);
    }
  };

  const updateComment = async (item, comment) => {
    try {
      await relancesAPI.update(item.id, { ...item, comment });
    } catch (error) {
      console.error('Failed to update comment', error);
    }
  };

  const updateCategory = async (item, category) => {
    try {
      await relancesAPI.update(item.id, { ...item, category });
      loadData();
    } catch (error) {
      console.error('Failed to update category', error);
    }
  };

  const handleDelete = async (item) => {
    try {
      await relancesAPI.delete(item.id);
      loadData();
    } catch (error) {
      console.error('Failed to delete', error);
    }
  };

  const filteredRelances = relances.filter((r) => {
    const matchCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchCategory;
  });

  const total = filteredRelances.length;

  const formatDate = (item) => {
    const day = item.day || 1;
    const monthIdx = parseInt(item.month) - 1;
    return `${day} ${MONTHS[monthIdx]} ${item.year}`;
  };

  return (
    <div className="relances-page">
      <div className="section-header">
        <div className="section-dot"></div>
        <h2>Relances Sheet</h2>
        <span className="count-badge">{total}</span>
        <div className="header-actions ml-auto">
          <label className="btn-import">
            <input type="file" accept=".csv,.xlsx" onChange={handleImportCSV} style={{ display: 'none' }} />
            {importing ? 'Importation...' : '📥 Importer CSV'}
          </label>
          <button className="btn-primary" onClick={() => openEditForm()}>+ Ajouter</button>
        </div>
      </div>

      {importStats && (
        <div className="import-stats">
          <span className="stats-success">✓ {importStats.imported} relances importees</span>
          {importStats.errors > 0 && <span className="stats-error"> | {importStats.errors} erreurs</span>}
        </div>
      )}

      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label className="drop-zone-content">
          <input type="file" accept=".csv,.xlsx" onChange={handleImportCSV} style={{ display: 'none' }} />
          <div className="drop-icon">{importing ? '⏳' : '📥'}</div>
          <div className="drop-text">
            {importing ? 'Importation en cours...' : 'Glissez un fichier CSV ou cliquez pour importer'}
          </div>
          <div className="drop-formats">Format: phone | date (JJ/MM) | commentaire | Annee: 2026</div>
        </label>
      </div>

      {showAddForm && (
        <div className="add-form" onClick={() => setShowAddForm(false)}>
          <div className="add-form-card" onClick={(e) => e.stopPropagation()}>
            <div className="add-form-header">
              <h3>{editingItem ? 'Modifier relance' : 'Nouvelle relance'}</h3>
              <button className="close-btn" onClick={() => setShowAddForm(false)}>×</button>
            </div>

            <div className="add-form-body">
              <div className="form-section">
                <label className="form-label">Numero de telephone</label>
                <input
                  type="text"
                  className="form-input phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="212661xxxxxx"
                />
              </div>

              <div className="form-section">
                <label className="form-label">Date de relance</label>
                <div className="date-row">
                  <div className="date-field">
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.day}
                      onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) || 1 })}
                      placeholder="JJ"
                    />
                    <span className="date-placeholder">JJ</span>
                  </div>
                  <span className="date-separator">/</span>
                  <div className="date-field">
                    <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>
                      {MONTHS.map((m, idx) => <option key={idx} value={idx + 1}>{m}</option>)}
                    </select>
                  </div>
                  <span className="date-separator">/</span>
                  <div className="date-field">
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                      {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Commentaire (optionnel)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Ex: interesse bulk, sans reponse..."
                />
              </div>
            </div>

            <div className="add-form-footer">
              <button className="btn-secondary" onClick={() => setShowAddForm(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave}>
                {editingItem ? 'Enregistrer' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportYearModal && (
        <div className="add-form" onClick={handleCancelImport}>
          <div className="add-form-card" onClick={(e) => e.stopPropagation()}>
            <div className="add-form-header">
              <h3>Selectionner l'annee d'import</h3>
              <button className="close-btn" onClick={handleCancelImport}>×</button>
            </div>

            <div className="add-form-body">
              <div className="form-section">
                <label className="form-label">Annee pour toutes les lignes</label>
                <input
                  ref={importYearInputRef}
                  type="number"
                  className="form-input"
                  value={importYear}
                  min="2000"
                  max="2100"
                  onChange={(e) => setImportYear(parseInt(e.target.value, 10) || new Date().getFullYear())}
                  placeholder="Ex: 2027"
                />
              </div>
            </div>

            <div className="add-form-footer">
              <button className="btn-secondary" onClick={handleCancelImport}>Annuler</button>
              <button className="btn-primary" onClick={handleConfirmImport}>Importer</button>
            </div>
          </div>
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label className="filter-label">Annee</label>
          <div className="year-tabs">
            {YEARS.map((y) => (
              <button key={y} className={`yb ${year === y ? 'active' : ''}`} onClick={() => setYear(y)}>{y}</button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Mois</label>
          <div className="month-chips">
            {MONTHS.map((m, idx) => (
              <div key={idx} className={`chip ${month === idx + 1 ? 'active' : ''}`} onClick={() => setMonth(idx + 1)}>{m}</div>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Statut</label>
          <div className="category-chips">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className={`chip ${categoryFilter === cat.key ? 'active' : ''}`}
                style={categoryFilter === cat.key && cat.color ? { background: cat.color, borderColor: cat.color } : {}}
                onClick={() => setCategoryFilter(cat.key)}
              >
                {cat.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="relances-table">
          <thead>
            <tr>
              <th>Numero</th>
              <th>Date</th>
              <th>Commentaire</th>
              <th>R1</th>
              <th>R2</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredRelances.length === 0 ? (
              <tr><td colSpan="6" className="no-data">Aucune relance</td></tr>
            ) : (
              filteredRelances.map((item) => (
                <tr key={item.id}>
                  <td className="phone-cell">{item.phone}</td>
                  <td className="date-cell">{formatDate(item)}</td>
                  <td>
                    <input
                      type="text"
                      defaultValue={item.comment || ''}
                      onBlur={(e) => updateComment(item, e.target.value)}
                      placeholder="Commentaire..."
                    />
                  </td>
                  <td>
                    <button className={`r-btn ${item.r1 ? 'done' : ''}`} onClick={() => toggleR1(item)}>
                      {item.r1 ? '✓' : '-'}
                    </button>
                  </td>
                  <td>
                    <button className={`r-btn ${item.r2 ? 'done' : ''}`} onClick={() => toggleR2(item)}>
                      {item.r2 ? '✓' : '-'}
                    </button>
                  </td>
                  <td>
                    <button className="delete-btn" onClick={() => handleDelete(item)}>×</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Relances;